/**
 * Progression — bandas, liberacoes e seletor de sessao.
 *
 * Seletor (secao 7), nesta ordem exata:
 *   filtra por estacao atual e construcoes existentes
 *   -> remove o que esta em cooldown
 *   -> sorteia por peso garantindo eixos_minimos
 *   -> intercala para nao repetir eixo em sequencia
 *
 * Regra dura: a banda sobe por ESTACAO CONCLUIDA, nunca por desempenho, e e
 * por EIXO, nunca por tarefa. Nao existe pontuacao aqui (D10) — nada neste
 * arquivo olha se a crianca "acertou".
 */

export class Progression {
  constructor(conteudo) {
    this.config = conteudo.progressao;
    this.tarefas = conteudo.tarefas;
    this.estacoes = conteudo.estacoes;
  }

  /** Monta a lista de tarefas da sessao de hoje. */
  selecionarSessao(estado) {
    const disponiveis = this.filtrarDisponiveis(estado);

    if (disponiveis.length === 0) {
      console.warn(
        `FAZENDA: nenhuma tarefa disponivel para ${estado.estacao} com as construcoes atuais. ` +
          'Confira se ha tarefas dessa estacao em conteudo/tarefas, ou se todas estao em cooldown.'
      );
      return [];
    }

    const [min, max] = this.config.tarefas_por_sessao;
    const quantidade = Math.min(disponiveis.length, aleatorioEntre(min, max));

    const sorteadas = this.sortearPorPeso(disponiveis, quantidade);
    return this.intercalarEixos(sorteadas);
  }

  /** Passo 1 e 2: estacao + construcoes existentes, depois cooldown. */
  filtrarDisponiveis(estado) {
    return this.tarefas.filter((tarefa) => {
      if (!tarefa.estacoes.includes(estado.estacao)) return false;

      // requer: so aparece se a construcao ja existe na fazenda
      for (const construcao of tarefa.requer ?? []) {
        if (!estado.construcoes[construcao]) return false;
      }

      // cooldown: guardado como o dia em que a tarefa volta a valer
      const voltaNoDia = estado.cooldowns[tarefa.id];
      if (voltaNoDia !== undefined && estado.dia < voltaNoDia) return false;

      return true;
    });
  }

  /** Passo 3: sorteio por peso, garantindo eixos_minimos eixos diferentes. */
  sortearPorPeso(disponiveis, quantidade) {
    const escolhidas = [];
    const restantes = [...disponiveis];
    const eixosUsados = new Set();
    const eixosMinimos = this.config.eixos_minimos;

    // Primeiro garante a variedade de eixos exigida.
    while (escolhidas.length < quantidade && eixosUsados.size < eixosMinimos) {
      const candidatas = restantes.filter((t) => !eixosUsados.has(t.eixo));
      if (candidatas.length === 0) break;

      const escolhida = sortearComPeso(candidatas);
      escolhidas.push(escolhida);
      eixosUsados.add(escolhida.eixo);
      restantes.splice(restantes.indexOf(escolhida), 1);
    }

    // Depois completa a sessao livremente.
    while (escolhidas.length < quantidade && restantes.length > 0) {
      const escolhida = sortearComPeso(restantes);
      escolhidas.push(escolhida);
      restantes.splice(restantes.indexOf(escolhida), 1);
    }

    if (eixosUsados.size < eixosMinimos) {
      console.warn(
        `FAZENDA: a sessao ficou com ${eixosUsados.size} eixos, abaixo dos ${eixosMinimos} ` +
          'exigidos em conteudo/progressao.json. Faltam tarefas de outros eixos nesta estacao.'
      );
    }

    return escolhidas;
  }

  /** Passo 4: nunca dois do mesmo eixo em sequencia. */
  intercalarEixos(tarefas) {
    if (!this.config.nunca_dois_seguidos_do_mesmo_eixo) return tarefas;

    const resultado = [];
    const pendentes = [...tarefas];

    while (pendentes.length > 0) {
      const ultimoEixo = resultado.at(-1)?.eixo;
      let indice = pendentes.findIndex((t) => t.eixo !== ultimoEixo);
      if (indice === -1) indice = 0; // so sobrou o mesmo eixo; segue assim
      resultado.push(pendentes[indice]);
      pendentes.splice(indice, 1);
    }

    return resultado;
  }

  /** Banda do eixo da tarefa. Banda e por eixo, nunca por tarefa (secao 7). */
  bandaDaTarefa(tarefa, estado) {
    return estado.bandas[tarefa.eixo] ?? 1;
  }

  /** Registra o cooldown depois que a tarefa foi feita. */
  registrarCooldown(tarefa, estado) {
    if (tarefa.cooldown > 0) {
      estado.cooldowns[tarefa.id] = estado.dia + tarefa.cooldown;
    }
  }

  /**
   * Fecha a sessao. Se a estacao completou, avanca estacao e sobe banda.
   * D7/D10: nada aqui depende de desempenho.
   */
  fecharSessao(estado) {
    estado.dia += 1;
    estado.sessoes_na_estacao += 1;

    const [minSessoes, maxSessoes] = this.config.sessoes_por_estacao;
    const alvo = aleatorioEntre(minSessoes, maxSessoes);

    if (estado.sessoes_na_estacao < alvo) return { mudouEstacao: false };

    estado.sessoes_na_estacao = 0;
    const ordem = this.estacoes.ordem;
    const indiceAtual = ordem.indexOf(estado.estacao);
    const proximo = (indiceAtual + 1) % ordem.length;
    estado.estacao = ordem[proximo];

    // Voltou para o inicio do ciclo: comecou um Ano novo.
    if (proximo === 0) {
      estado.ano += 1;
      const bandaDoAno = this.config.bandas_por_ano[String(estado.ano)];
      if (bandaDoAno) {
        for (const eixo of Object.keys(estado.bandas)) {
          estado.bandas[eixo] = bandaDoAno;
        }
      }
    }

    console.info(`FAZENDA: estacao mudou para ${estado.estacao} (ano ${estado.ano}).`);
    return { mudouEstacao: true, novaEstacao: estado.estacao };
  }

  /** Proxima construcao a liberar, na ordem da secao 7. */
  proximaConstrucao(estado) {
    const ordem = this.estacoes.construcoes_em_ordem_de_liberacao;
    return ordem.find((c) => !estado.construcoes[c]) ?? null;
  }
}

function aleatorioEntre(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function sortearComPeso(lista) {
  const total = lista.reduce((soma, t) => soma + (t.peso ?? 1), 0);
  let sorteio = Math.random() * total;
  for (const tarefa of lista) {
    sorteio -= tarefa.peso ?? 1;
    if (sorteio <= 0) return tarefa;
  }
  return lista.at(-1);
}
